// Workforce › Activity › Wake-ups (`/c/{slug}/agents/activity?state=wakeups`; UX pass 1.1 folded
// the old `/agents/wakeups` screen into Activity; ADR-0021 §2; M3-08): what is waiting to run and
// what admission did with each wake-up. "Queued" is the runs admission let in that no runtime has
// picked up yet (`runs.list?state=queued`). "Wake-ups and admission" is the event feed filtered
// to the wake-up and refusal events (`events.list`, managers): requested, folded into a run
// (coalesced), dropped (budget, paused, taken over, policy) and refused by a budget level (with
// its shortfall), and (DC-08) the ephemeral workers admission refused with the reason (no
// verbatim standing orders, depth, budget), newest first. Queued wake-ups themselves are read
// only by the runtime's key (`wakeups.pending`), so people see them as these events.
// WF-FIX (2026-10-26): a self-hosted DE's wake-ups stay in its inbox (never queued runs), so
// "Queued now" adds them (`wakeups.inbox`, managers) and lists them per DE.
// Calls on load: queued runs, the events and the inbox (managers), plus the cached DE list and
// company detail.
import {
  Badge,
  Button,
  EmptyState,
  InfoTip,
  Notice,
  StatGrid,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlarmClockIcon, BanIcon, InboxIcon, LayersIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import type { Api } from "../../api/client";
import type { EventRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { type CompanyAccess, isManager } from "../company";
import { formatDateTime } from "../format";
import { LoadError, Loading, Section } from "../screen-parts";
import { formatElapsed, formatMicros, useCurrency, WAKEUP_KIND_LABELS } from "./agent-parts";
import { runsQuery, wakeupEventsQuery, wakeupInboxQuery } from "./agent-queries";
import { RunsTable, useDeNames } from "./runs-screen";

const DROP_REASONS: Record<string, string> = {
  budget: "no budget left",
  taken_over: "a person took over",
  paused: "the DE is paused",
  policy: "policy",
  duplicate: "a duplicate",
  expired: "expired",
};

/** DC-08: why admission refused an ephemeral worker (`run.worker_refused`). */
export const WORKER_REFUSALS: Record<string, string> = {
  bundle_unavailable: "the DE has no deployed bundle to copy its standing orders from",
  standing_orders_missing: "the DE's bundle has no standing orders block",
  standing_orders_mismatch: "its standing orders were not the bundle's, verbatim",
  bundle_integrity_failed: "the deployed bundle failed its hash check",
  depth_exceeded: "it would delegate deeper than the policy allows",
  budget: "its budget is more than the run may still hand out",
  invalid_request: "the request was not valid",
};

const LEVELS: Record<string, string> = {
  company: "Company",
  team: "Team",
  seat: "Seat",
  holder: "DE",
  agent: "Agent",
};

type Line = { event: EventRow; agent: string | null; what: string; tone: Tone; label: string };
type Tone = "info" | "success" | "danger" | "warning";

/** The delegation request a wake-up came from, if it did. */
function delegationOf(e: EventRow): string | null {
  const ref = (e.payload as { source_ref?: { kind?: unknown; id?: unknown } | null }).source_ref;
  return ref?.kind === "delegation_request" && typeof ref.id === "string" ? ref.id : null;
}

/** One readable line per event (the payload shapes are the event catalog's). */
function lineOf(e: EventRow, names: Map<string, string>, currency: string | undefined): Line {
  const p = e.payload as Record<string, unknown>;
  const agentId = typeof p.agent_membership_id === "string" ? p.agent_membership_id : null;
  const agent = agentId ? (names.get(agentId) ?? "A digital employee") : null;
  switch (e.type) {
    case "wakeup.requested": {
      const kind = p.kind as keyof typeof WAKEUP_KIND_LABELS;
      return {
        event: e,
        agent,
        label: "Requested",
        tone: "info",
        what: WAKEUP_KIND_LABELS[kind] ?? String(kind),
      };
    }
    case "wakeup.coalesced":
      return {
        event: e,
        agent,
        label: "Folded in",
        tone: "success",
        what: "Joined a run already queued",
      };
    case "wakeup.dropped":
      return {
        event: e,
        agent,
        label: "Dropped",
        tone: "danger",
        what: `Not run: ${DROP_REASONS[String(p.reason)] ?? String(p.reason)}`,
      };
    case "budget.reservation_refused":
      return {
        event: e,
        agent,
        label: "Refused",
        tone: "warning",
        what: `${LEVELS[String(p.level)] ?? String(p.level)} budget short by ${formatMicros(Number(p.shortfall_micros), currency)} (needed ${formatMicros(Number(p.estimate_micros), currency)})`,
      };
    case "run.worker_refused":
      return {
        event: e,
        agent,
        label: "Worker refused",
        tone: "danger",
        what: `Worker not admitted: ${WORKER_REFUSALS[String(p.reason)] ?? String(p.reason)}`,
      };
    default:
      return { event: e, agent, label: e.type, tone: "info", what: e.type };
  }
}

/** Activity's Wake-ups state (UX pass 1.1; was its own screen, `/agents/wakeups`). */
export function WakeupsPanel({ api, company }: { api: Api; company: CompanyAccess }) {
  const manager = isManager(company);
  const queued = useInfiniteQuery(runsQuery(api, company.id, { state: "queued" }));
  const queuedCount = (queued.data?.pages ?? []).reduce((n, p) => n + p.data.length, 0);
  return (
    <div data-slot="wakeups">
      {manager ? (
        <Admission api={api} company={company} queued={queued.isSuccess ? queuedCount : null} />
      ) : null}
      <Section
        id="wakeups-queued"
        title="Queued"
        actions={
          <InfoTip label="About Queued">
            Admitted with their budget reserved, waiting for the runtime to pick them up.
          </InfoTip>
        }
      >
        <RunsTable
          api={api}
          company={company}
          state="queued"
          caption="Queued runs, newest first."
        />
      </Section>
      {manager ? null : (
        <div className="mt-8">
          <Notice tone="info" title="The admission log is for managers">
            Owners, admins and your CompanyOS operator see every wake-up and refusal.
          </Notice>
        </div>
      )}
    </div>
  );
}

function Admission({
  api,
  company,
  queued,
}: {
  api: Api;
  company: CompanyAccess;
  queued: number | null;
}) {
  const events = useInfiniteQuery(wakeupEventsQuery(api, company.id));
  const inbox = useQuery(wakeupInboxQuery(api, company.id));
  const names = useDeNames(api, company.id);
  const currency = useCurrency(api, company.id);
  const lines = useMemo(
    () =>
      (events.data?.pages ?? [])
        .flatMap((p) => p.data)
        .map((e) => lineOf(e, names, currency))
        .reverse(),
    [events.data, names, currency],
  );
  const dayAgo = Date.now() - 24 * 3_600_000;
  const recent = lines.filter((l) => Date.parse(l.event.occurred_at) >= dayAgo);
  const count = (label: string) => recent.filter((l) => l.label === label).length;
  // Follow the feed to its end (oldest first) so the newest events are on screen.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = events;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  return (
    <>
      <StatGrid data-slot="wakeup-stats">
        {/* WF-FIX: queued runs plus the wake-ups waiting in self-hosted DEs' inboxes. */}
        <StatTile
          label="Queued now"
          // An unreadable inbox is never counted as zero: the tile says it is incomplete.
          value={
            queued === null || inbox.isPending
              ? "…"
              : inbox.isError
                ? `${queued}+`
                : queued + inbox.data.total
          }
          icon={LayersIcon}
        />
        <StatTile label="Wake-ups, 24 h" value={count("Requested")} icon={AlarmClockIcon} />
        <StatTile label="Dropped, 24 h" value={count("Dropped")} icon={BanIcon} />
        <StatTile label="Refused by budget, 24 h" value={count("Refused")} icon={InboxIcon} />
      </StatGrid>
      {inbox.isError ? (
        <div
          role="alert"
          className="mt-4 flex items-center gap-2 text-sm text-danger-text"
          data-slot="wakeup-inbox-error"
        >
          Self-hosted inboxes could not be read, so Queued now leaves them out.
          <Button size="xs" variant="secondary" onClick={() => void inbox.refetch()}>
            Retry
          </Button>
        </div>
      ) : null}
      {inbox.data?.data.length ? (
        <Section
          id="wakeups-inbox"
          title="In self-hosted inboxes"
          actions={
            <InfoTip label="About self-hosted inboxes">
              A self-hosted DE picks its wake-ups up from its inbox (MCP my_inbox); until then they
              wait here, not as queued runs.
            </InfoTip>
          }
        >
          <ul className="m-0 flex list-none flex-col p-0" data-slot="wakeup-inbox">
            {inbox.data.data.map((r) => (
              <li
                key={r.membership_id}
                className="flex items-center gap-2 border-b border-subtle py-1.5 text-sm last:border-b-0"
                data-inbox={r.membership_id}
              >
                <WorkerBadge kind="de" size="sm" />
                <Link
                  to="/c/$slug/agents/de/$id"
                  params={{ slug: company.slug, id: r.membership_id }}
                  search={keepFocus}
                  className="min-w-0 truncate hover:underline"
                >
                  {workerName(r.display_name)}
                </Link>
                <Badge variant="outline">{r.queued} waiting</Badge>
                <span
                  className="ml-auto text-xs text-muted-foreground tabular-nums"
                  title={formatDateTime(r.oldest_requested_at)}
                >
                  oldest {formatElapsed(r.oldest_requested_at)} ago
                </span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      <Section
        id="wakeups-admission"
        title="Wake-ups and admission"
        actions={
          <InfoTip label="About admission">
            Newest first. A refusal names the budget level that had no room, or why a worker was not
            admitted.
          </InfoTip>
        }
      >
        {events.isPending ? (
          <Loading label="Loading wake-ups" rows={4} />
        ) : events.isError ? (
          <LoadError what="Wake-ups" error={events.error} />
        ) : !lines.length ? (
          <EmptyState
            variant="card"
            icon={AlarmClockIcon}
            title="No wake-ups yet"
            description="A mention, a task on a DE's seat, its heartbeat or a decided approval wakes it; each one shows here with what admission did."
          />
        ) : (
          <Table data-slot="wakeup-events">
            <TableCaption>Wake-up and admission events, newest first.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">When</TableHead>
                <TableHead scope="col">Digital employee</TableHead>
                <TableHead scope="col">Outcome</TableHead>
                <TableHead scope="col">Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.slice(0, 100).map((l) => (
                <TableRow key={l.event.id} data-event={l.event.type}>
                  <TableCell className="text-muted-foreground tabular-nums">
                    <span title={formatDateTime(l.event.occurred_at)}>
                      {formatElapsed(l.event.occurred_at)} ago
                    </span>
                  </TableCell>
                  <TableHead scope="row" className="font-medium">
                    {l.agent ?? "—"}
                  </TableHead>
                  <TableCell>
                    <Badge variant={l.tone}>{l.label}</Badge>
                  </TableCell>
                  <TableCell className="whitespace-normal">
                    {l.what}
                    {l.event.type === "wakeup.coalesced" &&
                    typeof (l.event.payload as { run_id?: unknown }).run_id === "string" ? (
                      <>
                        {" · "}
                        <Link
                          to="/c/$slug/agents/runs/$id"
                          params={{
                            slug: company.slug,
                            id: (l.event.payload as { run_id: string }).run_id,
                          }}
                          search={keepFocus}
                          className="text-primary-text underline-offset-4 hover:underline"
                        >
                          View run
                        </Link>
                      </>
                    ) : null}
                    {l.event.type === "run.worker_refused" && l.event.subject ? (
                      <>
                        {" · "}
                        <Link
                          to="/c/$slug/agents/runs/$id"
                          params={{ slug: company.slug, id: l.event.subject.id }}
                          search={keepFocus}
                          className="text-primary-text underline-offset-4 hover:underline"
                        >
                          View the lead run
                        </Link>
                      </>
                    ) : null}
                    {delegationOf(l.event) ? (
                      <>
                        {" · "}
                        <Link
                          to="/c/$slug/agents/delegations/$id"
                          params={{ slug: company.slug, id: delegationOf(l.event) as string }}
                          search={keepFocus}
                          className="text-primary-text underline-offset-4 hover:underline"
                        >
                          View request
                        </Link>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </>
  );
}
